<template>
  <div :class="$style.shop_detail">
    <!-- detail show -->
    <div v-if="storeRes" :class="$style.detail_area">
      <div :class="$style.detail_image">
        <div v-if="storeRes.image && storeRes.image.length > 1" :class="$style.slide_image_container">
          <div ref="carousel" :class="$style.carousel">
            <div :class="$style.container">
              <AtomsConsumerImage
                v-for="(imageUrl, index) in storeRes.image"
                :key="index"
                :src="imageUrl ?? ''"
                :class="$style.slide"
                :width="600"
              />
            </div>
          </div>
          <div :class="$style.dots">
            <div
              v-for="(_, index) in storeRes.image"
              :key="index"
              :class="[$style.dot, { [$style.active]: dotsSelectedIndex === index }]"
            />
          </div>
        </div>
        <div v-else-if="storeRes.image && storeRes.image.length == 1" :class="$style.slide_image_container">
          <div
            :class="$style.slide_image_item"
            :style="{
              background: `url('${ConvertImagePath(storeRes.image[0] ?? '')}') center center / cover`,
            }"
          />
        </div>
        <div v-else :class="$style.slide_image_container">
          <div :class="$style.slide_image_item">
            <AtomsConsumerImage :src="storePcImage" alt="" :class="$style.pc_image" :width="600" />
            <AtomsConsumerImage :src="storeSpImage" alt="" :class="$style.sp_image" :width="600" />
          </div>
        </div>
      </div>

      <div :class="$style.detail_introduce">
        <div :class="$style.detail_store_name">
          <div :class="$style.labels">
            <div v-if="storeRes.type === officialType" :class="$style.store_type_official">
              TENTIAL OFFICIAL STORE
            </div>
            <div v-if="storeRes.type === popupType" :class="$style.store_type_popup"> TENTIAL POP UP STORE </div>
            <div
              v-if="storeRes.label_text"
              :class="$style.shop_label"
              :style="{ color: storeRes.label_color, backgroundColor: storeRes.label_bg_color }"
            >
              {{ storeRes.label_text }}
            </div>
          </div>
          <h1 :class="$style.store_name">{{ storeRes.store_name }}</h1>
          <p v-if="storeRes.hours" :class="$style.store_hours">営業時間 {{ storeRes.hours }}</p>
          <div v-if="storeRes.post_code && storeRes.address" :class="$style.store_contact">
            <p v-if="storeRes.post_code">〒{{ storeRes.post_code }}</p>
            <p v-if="storeRes.address" :class="$style.shop_address">{{ storeRes.address }}</p>
          </div>
          <div v-if="storeRes.tell_number && storeRes.map_url" :class="$style.store_phone_map">
            <span v-if="storeRes.tell_number">
              <AtomsConsumerImage src="/images/site/shop-page/phone.svg" alt="" />
            </span>
            <AtomsConsumerLink v-if="storeRes.tell_number" :class="$style.tel" :url="`tel:${storeRes.tell_number}`">
              {{ storeRes.tell_number }}
            </AtomsConsumerLink>
            <span v-if="storeRes.map_url" :class="$style.address_svg">
              <AtomsConsumerImage src="/images/site/shop-page/map.svg" alt="" :class="$style.address_image" />
            </span>
            <AtomsConsumerLink v-if="storeRes.map_url" :class="$style.map" :url="storeRes.map_url" blank>
              Map
            </AtomsConsumerLink>
          </div>
          <div v-if="storeRes.products?.find((p) => isStoreProductTypeKey(p))" :class="$style.product">
            <p :class="$style.head">取扱商品</p>
            <div v-if="storeRes.type === 'official'" :class="$style.official">
              <p>幅広いラインナップ</p>
              <p :class="$style.note">※商品ページで店舗の在庫状況が確認可能です</p>
            </div>
            <div v-else :class="$style.series">
              <template v-for="product of storeRes.products" :key="product">
                <p v-if="isStoreProductTypeKey(product)">{{ StoreProductType[product] }}</p>
              </template>
            </div>
          </div>
          <div v-if="storeRes.describe" :class="$style.store_describe">
            <p v-html="sanitizeHtmlContent(storeRes.describe)"></p>
          </div>
        </div>
      </div>
    </div>

    <div :class="$style.tail_area">
      <div :class="$style.store_desc">
        <p>※お取り扱いアイテムは店舗によって異なります</p>
        <p>※取扱い商品についてのお問い合わせは直接店舗にご連絡ください。</p>
        <p>
          ※予告なしにお取り扱いがなくなる場合もございますので、在庫状況は店舗に直接ご確認いただいてからご来店いただくことをお薦めします。
        </p>
      </div>
      <div :class="$style.store_see">
        <AtomsConsumerLink url="/shop">&lt; 店舗一覧ページへ</AtomsConsumerLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import emblaCarouselVue from "embla-carousel-vue"
import Autoplay from "embla-carousel-autoplay"
import { sanitizeHtmlContent } from "@tential/ec-gql-schema/utils/functions/sanitize"
import { StoreProductType } from "@tential/ec-gql-schema/master/store"
import { ConvertImagePath } from "~/utils/functions/img"
import { usePublicStoreOneQuery } from "~/gql/urql.generated"
import { createStoreJsonLD, useCustomMeta } from "~/composables/meta"

const route = useRoute()
const customMeta = useCustomMeta()
const storeId = route.params.storeId as string

const officialType = "official"
const popupType = "popup"
const storePcImage = "../../images/common/no-image.png"
const storeSpImage = "../../images/common/no-image.png"

const [carousel, emblaApi] = emblaCarouselVue({ loop: true }, [Autoplay({ delay: 4000 })])
const dotsSelectedIndex = ref<number>(0)
watchEffect(() => {
  if (emblaApi.value) {
    // 初回のインデックスを設定
    dotsSelectedIndex.value = emblaApi.value.selectedScrollSnap()

    // autoplay, 手動でスライドを変更した際イベントをハンドリング -> インデックス更新
    emblaApi.value.on("select", (event) => {
      dotsSelectedIndex.value = event.selectedScrollSnap()
    })
  }
})

const { data: storeRes } = await useAsyncData("usePublicStoreOneQuery", async () => {
  const { data } = await usePublicStoreOneQuery({
    variables: { filter: { document_id: storeId } },
  })
  return data.value?.publicStoreOne
})

if (!storeRes.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Shop Not Found",
    fatal: process.client,
  })
}

const isStoreProductTypeKey = (key: any): key is keyof typeof StoreProductType => {
  return key in StoreProductType
}

useHead(
  customMeta.fetch({
    title: `${storeRes.value?.store_name} | TENTIAL取扱店詳細ページ`,
    jsonLDInfo: storeRes.value ? createStoreJsonLD(storeRes.value) : undefined,
  }),
)
</script>

<style lang="scss" module>
.shop_detail {
  .detail_area {
    text-align: center;
    @include md {
      max-width: $max-width-md;
      margin: 5.5rem auto 0;
      display: flex;
      gap: 2rem;
      align-items: flex-start;
    }
    div {
      &.detail_image {
        position: relative;
        display: inline-block;
        width: 100%;
        vertical-align: top;
        @include md {
          width: 600px;
        }
        .slide_image_container {
          position: relative;
          @include md {
            width: 600px;
          }
          .carousel {
            position: relative;
            overflow: hidden;
            .container {
              display: flex;
              .slide {
                flex: 0 0 100%;
                width: 100%;
                aspect-ratio: 375 / 280;
                object-fit: cover;
                @include md {
                  aspect-ratio: 600 / 449;
                }
              }
            }
          }

          .dots {
            display: flex;
            justify-content: center;
            gap: 0.5rem;
            margin-top: 1.25rem;

            .dot {
              width: 2.5rem;
              height: 0.25rem;
              background-color: $primary-20;
              border-radius: 4px;

              @include md {
                width: 3.75rem;
              }

              &.active {
                background-color: $primary;
              }
            }
          }
          .slide_image_item {
            position: relative;
            width: 100%;
            aspect-ratio: 375 / 280;
            object-fit: cover;
            @include md {
              aspect-ratio: 600 / 449;
            }
            img {
              width: 100%;
              object-fit: cover;
              aspect-ratio: 375 / 280;
              @include md {
                aspect-ratio: 600 / 449;
              }
            }
            .pc_image {
              display: none;
              @include md {
                display: inline;
              }
            }
            .sp_image {
              @include md {
                display: none;
              }
            }
          }
        }
      }
      &.detail_introduce {
        display: inline-block;
        padding: 0 1.25rem;
        margin-top: 4.75rem;
        text-align: left;
        width: 100%;
        @include md {
          min-height: 40px;
          padding: 0;
          margin-top: 0;
        }
        .detail_store_name {
          padding: 3rem 0 3.25rem;
          border-top: 0.5px solid $primary-20;
          border-bottom: 0.5px solid $primary-20;
          @include md {
            padding: 0;
            border: none;
          }
          .labels {
            display: flex;
            gap: 0.5rem;
            flex-wrap: wrap;
            align-items: flex-start;
            .store_type_official,
            .store_type_popup {
              padding: 0 0.75rem;
              font-size: $font-size-10;
              line-height: 2.6;
              margin-bottom: 0.5rem;
              background-color: $black-20;
              @include md {
                line-height: 2;
                font-size: $font-size-13;
                padding: 0.25rem 1rem;
              }
            }
            .shop_label {
              padding: 0 0.75rem;
              font-size: $font-size-10;
              line-height: 2.6;
              white-space: nowrap;
              @include md {
                line-height: 2;
                font-size: $font-size-13;
                padding: 0.25rem 1rem;
              }
            }
          }
          .store_name {
            font-size: $font-size-13;
            line-height: 1.85;
            font-family: $sub-font;
            font-weight: normal;
            @include md {
              font-size: $font-size-18;
              line-height: 1.44;
            }
          }
          .store_hours {
            height: 15px;
            margin-top: 1rem;
            font-size: $font-size-13;
            line-height: 15px;
          }
          .store_contact {
            min-height: 35px;
            margin-top: 1.25rem;
            font-size: $font-size-13;
            line-height: 15px;
            @include md {
              min-height: 39px;
              margin-top: 3.5rem;
              font-size: $font-size-14;
              line-height: 23px;
            }
            .shop_address {
              margin-top: 0.25rem;
            }
          }
          .store_phone_map {
            display: flex;
            align-items: center;
            height: 15px;
            margin: 0.75rem 0;
            font-size: $font-size-13;
            line-height: 15px;
            @include md {
              height: 19px;
              margin: 1.75rem 0;
              font-size: $font-size-15;
              line-height: 19px;
            }
            img {
              display: inline-block;
              width: 11px;
              height: 15px;
              @include md {
                width: 14px;
                height: 19px;
              }
            }
            .tel {
              min-width: 77px;
              height: 17px;
              margin-right: 0.75rem;
              margin-left: 0.25rem;
              line-height: 17px;
              @include md {
                min-width: 84px;
                margin-right: 1rem;
                margin-left: 0.5rem;
              }
            }
            .address_image {
              width: 10.65px;
              height: 15.57px;
              @include md {
                width: 13px;
                height: 19px;
              }
            }
            .map {
              width: 31px;
              height: 15px;
              margin-left: 4.35px;
              line-height: 15px;
              @include md {
                margin-left: 0.5rem;
              }
            }
            .map:hover {
              text-decoration-line: underline;
            }
          }
          .product {
            .head {
              font-size: $font-size-13;
              @include md {
                font-size: $font-size-15;
              }
            }
            .official {
              > p {
                font-size: $font-size-12;
                @include md {
                  font-size: $font-size-14;
                }
              }
              > .note {
                font-size: $font-size-10;
                @include md {
                  font-size: $font-size-12;
                }
              }
            }
            .series {
              display: flex;
              flex-wrap: wrap;
              gap: 0 0.5rem;
              > p {
                font-size: $font-size-12;
                width: fit-content;
                @include md {
                  font-size: $font-size-14;
                }
              }
            }
          }
          .store_describe {
            padding: 3rem 0 0px;
            margin-top: 3rem;
            font-size: $font-size-11;
            border-top: 0.5px solid $primary-20;
            border-bottom: 0.5px solid $primary-20;
            @include md {
              padding-bottom: 3rem;
              margin-top: 4rem;
              font-size: $font-size-12;
              border-bottom: none;
            }
            p {
              line-height: 23px;
              text-align: justify;
            }
          }
        }
      }
    }
  }
  .tail_area {
    padding: 0 1.25rem;
    font-size: $font-size-10;
    margin-top: 2.5rem;
    line-height: 1.8;
    @include md {
      max-width: $max-width-md;
      margin: 3.25rem auto 0;
      padding: 0;
      font-size: $font-size-12;
      line-height: 2.16;
    }
    .store_see {
      margin-top: 5rem;
      font-size: $font-size-12;
      text-align: center;
      cursor: pointer;
      text-decoration: underline;
      @include md {
        margin-top: 6.75rem;
        font-size: $font-size-15;
      }
    }
  }
}
</style>
